import { useEffect, useState } from 'react';
import { CompassMark } from '../components/CompassMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { LoadingTrail } from '../components/LoadingTrail';
import { hasAccountSession } from '../lib/accountSession';
import { ApiClientError } from '../lib/api';
import { StorefrontPage } from './StorefrontPage';
import { TrailBookPage } from './TrailBookPage';

export function AccountHomeFailure({
  message,
  onRetry,
  onShop,
}: {
  message: string;
  onRetry: () => void;
  onShop: () => void;
}) {
  return (
    <div className="app-shell home-shell account-home-failure-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Wild Trails home">
          <CompassMark />
          <span>Wild Trails</span>
        </a>
        <button type="button" className="parent-link" onClick={onShop}>
          Browse bracelets
        </button>
      </header>
      <main>
        <section className="account-home-failure" aria-labelledby="account-home-failure-title">
          <p className="eyebrow">Trail Book check</p>
          <h1 id="account-home-failure-title">We couldn’t open your Trail Book yet.</h1>
          <p>
            Your family account may still be signed in. Try the account check again, or browse the
            bracelets while the trail clears.
          </p>
          <ErrorNotice message={message} />
          <div className="account-home-failure-actions">
            <button type="button" className="trail-book-shop-link" onClick={onRetry}>
              Try again
            </button>
            <button type="button" className="parent-link" onClick={onShop}>
              Browse bracelets
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export function AccountAwareHomePage({ navigate }: { navigate: (path: string) => void }) {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    void hasAccountSession()
      .then((available) => {
        if (active) setSignedIn(available);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(
          reason instanceof ApiClientError
            ? reason.message
            : 'Wild Trails could not check your family account. Please try again.',
        );
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  if (error) {
    return (
      <AccountHomeFailure
        message={error}
        onRetry={() => {
          setSignedIn(null);
          setError(null);
          setAttempt((current) => current + 1);
        }}
        onShop={() => navigate('/shop')}
      />
    );
  }
  if (signedIn === null) return <LoadingTrail message="Finding your trail book…" />;
  if (!signedIn) return <StorefrontPage navigate={navigate} />;
  return (
    <TrailBookPage
      sessionValidated
      onOpenAnimal={(entitlementId) => navigate(`/trails/${entitlementId}`)}
      onOpenGrownupArea={() => navigate('/account')}
      onShop={() => navigate('/shop')}
    />
  );
}
